import * as XLSX from 'xlsx';
import { formatDateStable, formatTimeStable } from './formatUtils';
import { toast } from 'react-toastify';
import { formatHours } from './roundUtils';

const GROUPING_LABELS = {
  Parent: 'Parent',
  issue: 'Issue',
  project: 'Project',
  assignee: 'Assignee',
  author: 'Author',
  status: 'Status',
  priority: 'Priority',
};

const labelFor = (field) => GROUPING_LABELS[field] || field;

const sanitizeSheetName = (name) => {
  // Excel sheet names: max 31 chars, no : \ / ? * [ ]
  const cleaned = String(name).replace(/[:\\/?*[\]]/g, ' ').trim();
  return cleaned.slice(0, 31) || 'Sheet';
};

const buildGroupedSheet = (groupingField, groupedData) => {
  const groupLabel = labelFor(groupingField);
  const header = [
    groupLabel,
    'Summary',
    'Status',
    'Project',
    'Assignee',
    'Total Logged Hours',
    'Total Estimated',
    'Entries',
    'Unique Issues',
    'Efficiency %',
    'Variance (h)',
  ];
  const rows = [header];

  Object.entries(groupedData).forEach(([groupKey, group]) => {
    rows.push([
      groupKey,
      group.issue_summary || '',
      group.issue_status || '',
      group.issue_project_key || '',
      group.issue_assignee || 'Unassigned',
      formatHours(group.totalHours, 2),
      formatHours(group.totalEstimated, 2),
      Number(group.totalEntries) || 0,
      Number(group.uniqueIssuesCount) || 0,
      (Number(group.efficiency) || 0).toFixed(1),
      formatHours(group.variance, 2),
    ]);
  });

  return XLSX.utils.aoa_to_sheet(rows);
};

const buildDetailsSheet = (worklogs) => {
  const header = [
    'Issue',
    'Summary',
    'Status',
    'Project',
    'Assignee',
    'Author',
    'Work Date',
    'Work Time',
    'Created Date',
    'Logged Hours',
    'Comment',
  ];
  const rows = [header];

  (worklogs || []).forEach((log) => {
    const workDate = log.started ? formatDateStable(log.started) : (log.loggedDate || '');
    const workTime = log.started ? formatTimeStable(log.started) : '';
    const createdDate = log.created ? formatDateStable(log.created) : '';

    rows.push([
      log.issue_key || '',
      log.issue_summary || '',
      log.issue_status || '',
      log.issue_project_key || '',
      log.issue_assignee || 'Unassigned',
      log.author || 'Unknown',
      workDate,
      workTime,
      createdDate,
      typeof log.loggedHours === 'number' ? formatHours(log.loggedHours, 2) : '',
      log.comment || '',
    ]);
  });

  return XLSX.utils.aoa_to_sheet(rows);
};

export const handleExportToExcel = (groupingField, groupedWorkLogs, filteredWorkLogs) => {
  const isUngrouped = !groupingField || groupingField === 'none';

  if (isUngrouped) {
    if (!filteredWorkLogs || filteredWorkLogs.length === 0) {
      toast.warn('No data to export');
      return false;
    }

    const workbook = XLSX.utils.book_new();
    const detailsSheet = buildDetailsSheet(filteredWorkLogs);
    XLSX.utils.book_append_sheet(workbook, detailsSheet, sanitizeSheetName('Worklogs'));

    const dateStr = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `worklogs-${dateStr}.xlsx`);
    return true;
  }

  if (!groupedWorkLogs || Object.keys(groupedWorkLogs).length === 0) {
    toast.warn('No data to export');
    return false;
  }

  const groupLabel = labelFor(groupingField);
  const workbook = XLSX.utils.book_new();

  const groupedSheet = buildGroupedSheet(groupingField, groupedWorkLogs);
  XLSX.utils.book_append_sheet(
    workbook,
    groupedSheet,
    sanitizeSheetName(`Grouped by ${groupLabel}`)
  );

  const detailsSheet = buildDetailsSheet(filteredWorkLogs);
  XLSX.utils.book_append_sheet(
    workbook,
    detailsSheet,
    sanitizeSheetName('Worklog Details')
  );

  const dateStr = new Date().toISOString().split('T')[0];
  const fileSuffix = groupLabel.toLowerCase().replace(/\s+/g, '-');
  XLSX.writeFile(workbook, `worklogs-grouped-by-${fileSuffix}-${dateStr}.xlsx`);
  return true;
};
